/**
 * Pure fuzzy-search primitives — Levenshtein, n-gram, tokenization.
 * Moved from modules/settings/utils/ as part of the search infrastructure
 * generalization (Phase 1).
 */

export function levenshteinDistance(str1: string, str2: string): number {
  const track: number[][] = Array(str2.length + 1)
    .fill(null)
    .map(() => Array(str1.length + 1).fill(0));

  for (let i = 0; i <= str1.length; i += 1) track[0][i] = i;
  for (let j = 0; j <= str2.length; j += 1) track[j][0] = j;

  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1,
        track[j - 1][i] + 1,
        track[j - 1][i - 1] + indicator
      );
    }
  }
  return track[str2.length][str1.length];
}

export function stringSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  if (longer.length === 0) return 1.0;
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\säöüß]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 1);
}

export function containsAllTokens(text: string, tokens: string[]): boolean {
  const textLower = text.toLowerCase();
  return tokens.every((token) => textLower.includes(token));
}

export function fuzzyMatch(
  query: string,
  target: string,
  threshold = 0.8
): boolean {
  return stringSimilarity(query.toLowerCase(), target.toLowerCase()) >= threshold;
}

export function getNGrams(str: string, n = 3): Set<string> {
  const ngrams = new Set<string>();
  const cleaned = str.toLowerCase().replace(/\s+/g, '');
  for (let i = 0; i <= cleaned.length - n; i++) {
    ngrams.add(cleaned.substring(i, i + n));
  }
  return ngrams;
}

export function ngramSimilarity(str1: string, str2: string): number {
  const a = getNGrams(str1);
  const b = getNGrams(str2);
  if (a.size === 0 && b.size === 0) return 0;
  const intersection = new Set([...a].filter((x) => b.has(x)));
  const union = new Set([...a, ...b]);
  return union.size > 0 ? intersection.size / union.size : 0;
}
